/**
 * 评测配置 › 体验评分（`#eval-cx`，后台页面对齐批 3 B 路；只读）：CX 体验判官的口径。
 * 判官逻辑在 `plan_lab/eval/cx.py`（七条理由、合规封顶、待复核），提示词与模型从题库快照取；
 * 1～5 分各代表什么取自提示词「合成 1-5」一节（后端把英文简写换成七条理由的中文名，批 4；表格换行显示全文）。观测列，不进通过率。改提示词走 Loop 侧再导出新快照。
 */
import { useEffect, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CodeBlock } from "@/components/ui/code-block";
import { EmptyState } from "@/components/ui/feedback";
import { type PlanBankCx, usePlanBankCx, usePlanBanks } from "@/lib/api/plan-eval-config";

import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import { BankPicker, BlockState, Field } from "./shared";

type Reason = PlanBankCx["reasons"][number];
type NameDesc = { name: string; desc: string };

const SCORE_TONE: Record<number, BadgeTone> = {
	1: "danger",
	2: "danger",
	3: "warning",
	4: "success",
	5: "success",
};

function Title({ children, sub }: { children: React.ReactNode; sub?: React.ReactNode }) {
	return (
		<div className="space-y-0.5">
			<div className="text-sm font-semibold text-text-primary">{children}</div>
			{sub ? <div className="text-xs text-text-muted">{sub}</div> : null}
		</div>
	);
}

const NAME_DESC_COLS: PlanColumn<NameDesc>[] = [
	{ key: "n", title: "看什么", className: "w-36", render: (r) => r.name },
	{
		key: "d",
		title: "怎么算 ／ 怎么用",
		className: "max-w-[560px]",
		render: (r) => (
			<span className="block truncate text-text-secondary" title={r.desc}>
				{r.desc}
			</span>
		),
	},
];

export function EvalCxTab() {
	const list = usePlanBanks();
	const rows = list.data?.rows ?? [];
	const [bankId, setBankId] = useState("");
	useEffect(() => {
		const first = rows[0];
		if (!bankId && first) setBankId(first.bank_id);
	}, [bankId, rows]);
	const id = bankId || rows[0]?.bank_id || null;
	const q = usePlanBankCx(id);

	if (list.isLoading || list.isError || !list.data)
		return <BlockState testId="plan-block-cx-head" q={list} failTestId="plan-cx-fail" />;
	if (!rows.length || !id)
		return (
			<Card data-testid="plan-block-cx-head">
				<CardContent>
					<EmptyState
						title="还没有题库快照"
						desc="CX 判官的提示词和模型从题库快照取，先导出一份。"
					/>
				</CardContent>
			</Card>
		);

	const d = q.data;
	const head = (
		<Card data-testid="plan-block-cx-head">
			<CardContent className="space-y-3 py-4">
				<div className="flex flex-wrap items-center gap-3">
					<BankPicker rows={rows} value={id} onChange={setBankId} />
					<span className="text-xs text-text-muted">
						只读：判官提示词随 <PageJump to="eval-bank" />
						，改动走 Loop 侧再导出
					</span>
				</div>
				{d ? (
					<div
						className="grid gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-4"
						data-testid="plan-cx-meta"
					>
						<Field label="判官模型">
							<span className="font-mono">{d.model || "—"}</span>
						</Field>
						<Field label="进不进通过率">
							<Badge tone="neutral">{d.counts_in_pass_rate ? "进" : "不进，观测列"}</Badge>
						</Field>
						<Field label="评什么">
							每段完整对话一次：七条理由逐条独立判，再合成 1～5 分和一句引用原文的解释
						</Field>
						<Field label="这份快照">
							{d.enabled ? "带 CX 提示词，跑批会评 CX" : "没有 CX 提示词，用它跑的批次不评 CX"}
						</Field>
					</div>
				) : null}
			</CardContent>
		</Card>
	);
	if (q.isLoading || q.isError || !d)
		return (
			<>
				{head}
				<BlockState testId="plan-block-cx-body" q={q} failTestId="plan-cx-body-fail" />
			</>
		);

	const reasonCols: PlanColumn<Reason>[] = [
		{
			key: "label",
			title: "理由",
			className: "w-32",
			render: (r) => <span className="font-medium">{r.label}</span>,
		},
		{
			key: "key",
			title: "键",
			className: "w-56",
			render: (r) => <span className="font-mono text-xs text-text-muted">{r.key}</span>,
		},
		{ key: "values", title: "取值", className: "w-56", render: (r) => r.values.join(" ／ ") },
		{
			key: "neg",
			title: "负面方向",
			render: (r) => (
				<span className="inline-flex items-center gap-2">
					<Badge tone="danger">{r.negative_when} ＝ 负面</Badge>
					{r.negative_when === "High" ? (
						<span className="text-xs text-text-muted">方向相反：High 是体验差</span>
					) : null}
				</span>
			),
		},
	];

	return (
		<>
			{head}
			<div className="grid gap-4 xl:grid-cols-2" data-testid="plan-block-cx-body">
				<Card data-testid="plan-cx-anchors">
					<CardContent className="space-y-3 py-4">
						<Title sub="方向锚点，不是公式：判官综合七条理由的取值来定分">1～5 分各代表什么</Title>
						{d.anchors.length ? (
							<PlanTable
								columns={[
									{
										key: "s",
										title: "分",
										className: "w-14",
										render: (a: { score: number; desc: string }) => (
											<Badge tone={SCORE_TONE[a.score] ?? "muted"}>{a.score}</Badge>
										),
									},
									{
										key: "d",
										title: "典型情形",
										className: "whitespace-normal py-2",
										render: (a: { score: number; desc: string; raw?: string }) => (
											<span className="block break-words" title={a.raw}>
												{a.desc}
											</span>
										),
									},
								]}
								rows={d.anchors}
								rowKey={(a) => String(a.score)}
								density="compact"
								rowTestId="plan-cx-anchor-row"
							/>
						) : (
							<div className="text-sm text-text-muted">这份快照没有 CX 提示词，没有评分口径</div>
						)}
						<div className="text-xs text-text-secondary">
							硬规则：合规风险为{d.score_cap.when === "High" ? "高" : ` ${d.score_cap.when} `}
							时分数封顶 {d.score_cap.cap} 分（代码兜底，原始分留在记录里）。
						</div>
					</CardContent>
				</Card>
				<Card data-testid="plan-cx-review">
					<CardContent className="space-y-3 py-4">
						<Title sub="只标记、不改分；被标的记录是人工复核的第一批样本">待复核规则</Title>
						<PlanTable
							columns={[
								{
									key: "id",
									title: "规则",
									className: "w-14",
									render: (r: { id: string; desc: string }) => <Badge tone="warning">{r.id}</Badge>,
								},
								{
									key: "d",
									title: "什么情况标",
									className: "max-w-[480px]",
									render: (r: { id: string; desc: string }) => (
										<span className="block truncate" title={r.desc}>
											{r.desc}
										</span>
									),
								},
							]}
							rows={d.review_rules}
							rowKey={(r) => r.id}
							density="compact"
						/>
					</CardContent>
				</Card>
			</div>
			<Card data-testid="plan-block-cx-reasons">
				<CardContent className="space-y-3 py-4">
					<Title sub="逐条独立判，证据不足一律 Neutral；负面方向只在代码里定义一处">七条理由</Title>
					<PlanTable
						columns={reasonCols}
						rows={d.reasons}
						rowKey={(r) => r.key}
						density="compact"
						rowTestId="plan-cx-reason-row"
					/>
				</CardContent>
			</Card>
			<div className="grid gap-4 xl:grid-cols-2" data-testid="plan-block-cx-report">
				<Card>
					<CardContent className="space-y-3 py-4">
						<Title sub="评测报告「CX 体验」一节；通过不等于体验好">批次报告里怎么看</Title>
						<PlanTable
							columns={NAME_DESC_COLS}
							rows={d.report_metrics}
							rowKey={(r) => r.name}
							density="compact"
							rowTestId="plan-cx-metric-row"
						/>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="space-y-3 py-4">
						<Title
							sub={`只看对话与留痕、不调模型，和 CX 一起看；相似度阈值 ${d.experience.similarity}`}
						>
							体验结构信号
						</Title>
						<PlanTable
							columns={NAME_DESC_COLS}
							rows={d.experience.signals}
							rowKey={(r) => r.name}
							density="compact"
						/>
					</CardContent>
				</Card>
			</div>
			<Card data-testid="plan-block-cx-prompt">
				<CardContent className="space-y-3 py-4">
					<Title sub="{{conversation}} 填整段对话，{{aux_block}} 填辅助信号（客户目标、实际提交、终止方式；不给金标）">
						判官提示词
					</Title>
					{d.prompt_keys.map((k) => (
						<div key={k} className="space-y-1.5">
							<div className="font-mono text-xs text-text-muted">{k}</div>
							{d.prompts[k] ? (
								<CodeBlock code={d.prompts[k] ?? ""} maxHeight={480} />
							) : (
								<div className="text-sm text-text-muted">这份快照里没有</div>
							)}
						</div>
					))}
				</CardContent>
			</Card>
		</>
	);
}
